/**
 * Quote hooks — today's quote + like/unlike with optimistic update.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchTodayQuote, likeQuote, unlikeQuote } from '@turnifly/shared/api'

export const quoteKeys = {
  today: (userId: string) => ['quote', 'today', userId] as const,
} as const

// ── Today's Quote ───────────────────────────────────────────

export function useTodayQuote(userId: string | undefined) {
  return useQuery({
    queryKey: quoteKeys.today(userId ?? ''),
    queryFn: () => fetchTodayQuote(userId!),
    enabled: !!userId,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  })
}

// ── Like / Unlike Quote ─────────────────────────────────────

interface ToggleQuoteLikeParams {
  userId: string
  quoteId: string
  isLiked: boolean // current state — true means user wants to UNLIKE
}

export function useToggleQuoteLike() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, quoteId, isLiked }: ToggleQuoteLikeParams) => {
      if (isLiked) {
        await unlikeQuote(userId, quoteId)
      } else {
        await likeQuote(userId, quoteId)
      }
    },
    onMutate: async ({ userId, isLiked }) => {
      const key = quoteKeys.today(userId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)

      // Optimistic: toggle like state and adjust count
      queryClient.setQueryData(key, (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        const quote = old as Record<string, unknown>
        return {
          ...quote,
          is_liked: !isLiked,
          likes_count: ((quote.likes_count as number) ?? 0) + (isLiked ? -1 : 1),
        }
      })

      return { previous, key }
    },
    onError: (_err, _params, context) => {
      if (context?.previous && context?.key) {
        queryClient.setQueryData(context.key, context.previous)
      }
    },
    onSettled: (_data, _error, { userId }) => {
      queryClient.invalidateQueries({ queryKey: quoteKeys.today(userId) })
    },
  })
}
